import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const mouansSartouxEn: ChauffeurPage = {
  slug: "mouans-sartoux",
  title: "VTC & private chauffeur in Mouans-Sartoux",
  subtitle:
    "Private chauffeur in Mouans-Sartoux: the Espace de l’Art Concret and its château, between Cannes and Grasse, with Nice airport transfers.",
  keywords: [
    "vtc mouans-sartoux",
    "private chauffeur mouans-sartoux",
    "mouans sartoux airport transfer",
    "cannes grasse chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Mouans-Sartoux",
    subtitle:
      "Between Cannes and Grasse, the village of the Espace de l’Art Concret and its château: Nice airport transfers and hourly hire.",
    keywords: [
      "VTC Mouans-Sartoux",
      "chauffeur between Cannes and Grasse",
      "Nice airport transfer",
      "Espace de l’Art Concret chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Mouans-Sartoux",
    badges: ["Between Cannes & Grasse", "Concrete Art", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Mouans-Sartoux",
    items: [
      {
        title: "Between Cannes and Grasse",
        description:
          "A pivotal position on the Cannes–Grasse axis: the chauffeur links the Croisette, the perfume capital and the back country with no route worry.",
      },
      {
        title: "Espace de l’Art Concret & château",
        description:
          "Drop-off in front of the château of Mouans and the contemporary art centre, with a dedicated chauffeur who waits during the visit.",
      },
      {
        title: "Nice airport ~35 min",
        description:
          "Reach Nice Côte d’Azur airport in about 35 minutes via the A8, with flight tracking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "FR/EN, based on the French Riviera, punctual and discreet — 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Mouans-Sartoux",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up in Mouans-Sartoux with flight tracking, to and from Nice Côte d’Azur airport, in about 35 minutes.",
      },
      {
        title: "Cannes & Grasse link",
        text: "Shuttles to the Croisette and to Grasse, the perfume capital, with a chauffeur who anticipates traffic on the axis.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day: Espace de l’Art Concret, the Grasse perfumeries, Mougins and the neighbouring villages.",
      },
      {
        title: "Culture & contemporary art",
        text: "Drop-off at the château of Mouans and the Espace de l’Art Concret, waiting during the visit and pick-up on request.",
      },
      {
        title: "Long distance",
        text: "Journeys of any distance from Mouans-Sartoux: Nice, Monaco, Saint-Tropez, Italy and ski resorts, in a comfortable vehicle.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Château of Mouans",
        description: "Triangular château home to the Espace de l’Art Concret.",
      },
      {
        name: "Espace de l’Art Concret",
        description:
          "Contemporary art centre and the Albers-Honegger donation.",
      },
      {
        name: "Mouans-Sartoux village",
        description: "Heart of the village, market and shops.",
      },
      {
        name: "Grasse",
        description: "The perfume capital, a few minutes away by road.",
      },
      {
        name: "Cannes / Croisette",
        description: "Towards the sea and the Palais des Festivals.",
      },
      {
        name: "Nice airport",
        description: "About 35 minutes via the A8.",
      },
    ],
  },
  generic: {
    heading: "Mouans-Sartoux, contemporary art between Cannes and Grasse",
    subheading:
      "A pivotal village on the Cannes–Grasse axis, known for its château and the Espace de l’Art Concret.",
    paragraphs: [
      "Mouans-Sartoux holds a pivotal position between Cannes and Grasse: its triangular château houses the Espace de l’Art Concret and its contemporary art donation, a cultural landmark of the area. The chauffeur drops you in front of the château or in the heart of the village and waits during the visit, with no parking constraint.",
      "The village is also a practical base for exploring: the Cannes Croisette on one side, the Grasse perfumeries on the other, Mougins and Valbonne within reach by road. Our chauffeurs anticipate traffic on the axis to keep to your schedule.",
      "For Nice Côte d’Azur airport, allow around 35 minutes via the A8 depending on traffic; our bilingual chauffeurs track your flight and adjust the departure.",
    ],
    bullets: [
      "Cannes–Grasse axis mastered",
      "Drop-off at château & art centre",
      "Flight tracking & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Mouans-Sartoux",
    steps: [
      "Share your route (village, Cannes, Grasse, Nice airport) and your times.",
      "We confirm the right vehicle and a fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you at the agreed address.",
      "Live adjustments to suit traffic and your programme.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Mouans-Sartoux"),
    {
      q: "How long is it to Nice airport?",
      a: "Allow about 35 minutes via the A8 depending on traffic, with flight tracking and a personal welcome.",
    },
    {
      q: "Do you serve Cannes and Grasse from Mouans-Sartoux?",
      a: "Yes, the village sits on the Cannes–Grasse axis: we provide links to the Croisette and to the Grasse perfumeries, with a chauffeur who anticipates traffic.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the Cannes events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class sedans, 7-seater van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can I visit the Espace de l’Art Concret with a chauffeur?",
      a: "Yes, we drop you in front of the château of Mouans and the Espace de l’Art Concret, waiting during the visit and picking you up on request.",
    },
  ],
  relatedLinks: [
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
    {
      label: "Car service on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Nice Airport shuttle", href: "/en/chauffeur/airport" },
    {
      label: "Short-term car rental in Grasse",
      href: "/en/location/car-rental-grasse",
    },
  ],
  cta: {
    title: "Your private chauffeur in Mouans-Sartoux",
    text: "Nice airport transfer, Cannes–Grasse link or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Mouans-Sartoux — car service & transfer",
    description:
      "Private car service in Mouans-Sartoux: Nice airport transfer in ~35 min, Espace de l’Art Concret, Cannes–Grasse link. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/mouans-sartoux",
  },
};

export default mouansSartouxEn;
